/**
 * TicketCtaButton — the ticket card's action button, in its two placements.
 *
 * Extracted from `BookingTicketCard` (S3) so the demo ticket card renders the
 * identical control: `desktop` is the inline pill; `floating-mobile` portals
 * to `<body>` so the TicketCard's CSS mask can't clip it — a mask makes the
 * card the containing block for fixed descendants, which trapped the button
 * inside the ticket shape. ClassNames are the originals, verbatim.
 *
 * @module pages/booking/shared/TicketCtaButton
 */

import { createPortal } from 'react-dom';
import { ArrowIcon } from '@butter/ui/icons';

interface TicketCtaButtonProps {
  label: string;
  ariaLabel: string;
  onClick?: () => void;
  disabled: boolean;
  /** Booking creation in flight — renders the spinner + busyLabel. */
  busy: boolean;
  busyLabel: string;
  /** `floating-mobile` portals to document.body; only ONE instance per page may use it. */
  variant: 'desktop' | 'floating-mobile';
  /** Test hook — e2e binds the demo CTAs through this. */
  dataTestId?: string;
}

export function TicketCtaButton({ label, ariaLabel, onClick, disabled, busy, busyLabel, variant, dataTestId }: TicketCtaButtonProps) {
  if (variant === 'floating-mobile') {
    return createPortal(
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        className="fixed bottom-mobile-cta left-4 right-4 z-10 flex items-center justify-center gap-2 rounded-full bg-theme-600 py-3 pl-6 pr-2 text-base font-medium text-white shadow-lg transition-colors hover:bg-theme-700 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-theme-600 md:hidden"
      >
        {busy ? (
          <>
            <div
              className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"
              role="status"
              aria-label="Creating booking"
            />
            {busyLabel}
          </>
        ) : (
          <>
            <span className="flex-1 text-center">{label}</span>
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white text-theme-600">
              <ArrowIcon className="size-5" ariaHidden strokeWidth={1.5} />
            </span>
          </>
        )}
      </button>,
      document.body,
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className="flex items-center gap-5 rounded-full bg-theme-600 px-6 py-3 text-base font-medium text-white shadow-sm transition-colors hover:bg-theme-700 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-theme-600"
    >
      {busy ? (
        <>
          <div
            className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"
            role="status"
            aria-label="Creating booking"
          />
          {busyLabel}
        </>
      ) : (
        <>
          {label}
          <div className="bg-white rounded-full p-1">
            <ArrowIcon
              className="size-4 text-theme-600"
              ariaHidden
              strokeWidth={1.2}
            />
          </div>
        </>
      )}
    </button>
  );
}
